import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { PurchaseMoneyBar } from '@/components/purchases/purchase-money-bar';
import { PurchaseTable } from '@/components/purchases/purchase-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { PERIODS, periodStart } from '@/lib/dates';
import { useOrgId } from '@/lib/org';
import { usePurchaseTransactions, type PurchaseRow } from '@/lib/purchases';
import { cn } from '@/lib/utils';

type Chip = 'all' | 'draft' | 'unpaid' | 'overdue' | 'not_due' | 'partially_paid' | 'paid' | 'void';
const CHIPS: Array<[Chip, string]> = [
  ['all', 'All'],
  ['draft', 'Draft'],
  ['unpaid', 'Unpaid'],
  ['overdue', 'Overdue'],
  ['not_due', 'Not due yet'],
  ['partially_paid', 'Partially paid'],
  ['paid', 'Paid'],
  ['void', 'Void'],
];
const open = (r: PurchaseRow) => r.status === 'approved' || r.status === 'partially_paid';
const MATCH: Record<Chip, (r: PurchaseRow) => boolean> = {
  all: () => true,
  draft: (r) => r.status === 'draft',
  unpaid: open,
  overdue: (r) => open(r) && r.overdue,
  not_due: (r) => open(r) && !r.overdue,
  partially_paid: (r) => r.status === 'partially_paid',
  paid: (r) => r.status === 'paid',
  void: (r) => r.status === 'void',
};

export const Route = createFileRoute('/expenses/bills/')({
  component: Bills,
  validateSearch: (s: Record<string, unknown>): { status?: Chip } =>
    typeof s['status'] === 'string' && s['status'] in MATCH ? { status: s['status'] as Chip } : {},
});

/** Bills list with money bar and status chips (document 06 E2). */
function Bills() {
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const { status = 'all' } = Route.useSearch() as { status?: Chip };
  const [q, setQ] = useState('');
  const [period, setPeriod] = useState('all');
  const [bar, setBar] = useState<string | null>(null);
  const tx = usePurchaseTransactions();
  const needle = q.trim().toLowerCase();
  const since = periodStart(period);
  const rows = (tx.data ?? []).filter(
    (r) =>
      r.type === 'bill' &&
      MATCH[status](r) &&
      (!bar || (bar === 'paid' ? r.status === 'paid' : MATCH[bar as Chip](r))) &&
      (!since || r.date >= since) &&
      (!needle ||
        (r.contactName ?? '').toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle) ||
        (r.reference ?? '').toLowerCase().includes(needle) ||
        (r.memo ?? '').toLowerCase().includes(needle)),
  );
  const setStatus = (s: Chip) =>
    void navigate({ to: '/expenses/bills', search: s === 'all' ? {} : { status: s } });
  const actions = me.data?.isStaff ? (
    <div className="flex gap-2">
      <Button variant="outline" asChild>
        <Link to="/expenses/pay-bills">Pay bills</Link>
      </Button>
      <Button asChild>
        <Link to="/expenses/bills/new">New bill</Link>
      </Button>
    </div>
  ) : null;
  return (
    <>
      <PageHeader title="Bills" actions={actions} />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <>
          <PurchaseMoneyBar active={bar} onSelect={setBar} />
          <div
            className="mb-3 flex flex-wrap items-center gap-2"
            role="tablist"
            aria-label="Status"
          >
            {CHIPS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={status === k}
                onClick={() => setStatus(k)}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  status === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="mb-3 flex items-center gap-4">
            <div className="relative w-[340px]">
              <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card pl-8"
                placeholder="Search by vendor, bill no. or memo"
                aria-label="Search bills"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <NativeSelect
              aria-label="Date"
              className="w-[200px] bg-card"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : tx.isError ? (
            <EmptyState title="Could not load bills" hint={tx.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={
                (tx.data ?? []).some((r) => r.type === 'bill')
                  ? 'No bills match these filters'
                  : 'No bills yet'
              }
              action={actions ?? undefined}
            />
          ) : (
            <PurchaseTable rows={rows} showType={false} />
          )}
        </>
      )}
    </>
  );
}
